import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import type { ParsedProduct } from '@/hooks/useProducts';
import Footer from '@/components/feature/Footer';
import SignupRequiredModal from '@/components/feature/SignupRequiredModal';
import { useProducts } from '@/hooks/useProducts';
import { productCategories } from '@/mocks/productCategories';
import { getSupabaseClient } from '@/lib/supabase';
import { consumePendingImageSearchFile } from '@/lib/pendingImageSearch';
import { createImageSearchJobApi, pollImageSearchJobApi, normalizeImageForSearch } from '@/lib/imageSearchService';
import type { ImageSearchJobApi } from '@/lib/imageSearchService';
import SearchHeader from './components/SearchHeader';
import CategorySidebar from './components/CategorySidebar';
import FilterToolbar from './components/FilterToolbar';
import ProductResults from './components/ProductResults';
import BottomCTA from './components/BottomCTA';


// ─── Edge Function result item → ParsedProduct transformer ───

const MATERIAL_KEYWORDS: Record<string, string[]> = {
  Glass: ['유리', '글라스'],
  Aluminum: ['알루미늄'],
  Metal: ['금속'],
  PET: ['PET'],
  PETG: ['PETG'],
};

function parseCapacity(name: string): string | null {
  const match = name.match(/(\d+)\s*ml/i);
  return match ? `${match[1]}ml` : null;
}

function parseMaterial(name: string): string | null {
  for (const [material, keywords] of Object.entries(MATERIAL_KEYWORDS)) {
    if (keywords.some((kw) => name.includes(kw))) {
      return material;
    }
  }
  return null;
}

function edgeResultToParsedProduct(raw: Record<string, unknown>): ParsedProduct {
  const tags = raw.tags as Record<string, unknown> | null;
  const specs = tags?.specs as Record<string, unknown> | null;
  const name = (raw.name as string) || '';
  const categoryL1Code = (tags?.category_l1_code as string | undefined) || null;
  const categoryL2Code = (tags?.category_l2_code as string | undefined) || null;

  // Edge function may return an array of image URLs; keep them for fallback
  const edgeImages = (raw.images as string[] | undefined) || [];
  const edgeImageUrl = (raw.image_url as string | undefined) || (raw.imageUrl as string | undefined) || null;
  const enrichedTags = edgeImages.length > 0
    ? { ...(tags || {}), _imageUrls: edgeImages }
    : tags;

  return {
    id: raw.id as string,
    name,
    sku: raw.sku as string | null,
    category: raw.category as string | null,
    status: (raw.status as string) || 'active',
    imageUrl: edgeImageUrl,
    createdAt: '',
    updatedAt: '',
    capacity: parseCapacity(name),
    materialHint: parseMaterial(name),
    mappedCategory: categoryL2Code,
    categoryL2Code,
    categoryL1Code,
    availabilityStatus: null,
    availabilityDisplay: null,
    availabilityBadgeType: null,
    availabilityNote: null,
    moq: null,
    moqDisplay: null,
    leadTimeMinDays: null,
    leadTimeMaxDays: null,
    leadTimeDisplay: null,
    productKind: null,
    deliverDays: null,
    capacityMl: (specs?.capacity_ml as string | undefined) || null,
    capacityMlTag: null,
    moqTag: null,
    similarity: (raw.similarity as number | undefined) ?? null,
    tags: enrichedTags,
    displayPriceText: null,
    samplePriceText: null,
  };
}

interface JobData {
  id: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: {
    step: string | null;
    message: string | null;
    percent: number;
  };
  error: {
    code: string | null;
    message: string | null;
  } | null;
  result: Record<string, unknown> | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

function mapApiJobToJobData(apiJob: ImageSearchJobApi): JobData {
  return {
    id: apiJob.id,
    type: apiJob.type,
    status: apiJob.status,
    progress: apiJob.progress,
    error: apiJob.error,
    result: apiJob.result,
    created_at: apiJob.created_at,
    started_at: apiJob.started_at,
    completed_at: apiJob.completed_at,
  };
}



export default function ProductsPage() {
  const { t, i18n } = useTranslation('products');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [searchMode, setSearchMode] = useState<'browse' | 'text' | 'image'>('browse');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pendingTextSearch, setPendingTextSearch] = useState(false);
  const [searchStatus, setSearchStatus] = useState<{
    message: string;
    type: 'loading' | 'success' | 'error';
  } | null>(null);
  const [overrideProducts, setOverrideProducts] = useState<ParsedProduct[] | null>(null);

  // Image search job state
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tRef = useRef(t);
  tRef.current = t;
  const handleImageSearchRef = useRef<typeof handleImageSearch>(null!);

  // =========================
  // Legacy: restore completed results from homepage navigation
  // =========================
  useEffect(() => {
    const stored = sessionStorage.getItem('imageSearchResults');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const prods = (parsed.products || []) as ParsedProduct[];
        const total = parsed.total ?? prods.length;
        if (prods.length > 0) {
          setOverrideProducts(prods);
          setSearchMode('image');
          setSearchStatus({
            message: t('products.imageSearch.completed', { count: total }) || `${total} similar products found from your uploaded image.`,
            type: 'success',
          });
        }
        sessionStorage.removeItem('imageSearchResults');
      } catch {
        // ignore
      }
    }
  }, [location.pathname, t]);

  // =========================
  // Legacy: auto-poll pending job navigated from homepage
  // =========================
  useEffect(() => {
    const pendingJobId = sessionStorage.getItem('pendingJobId');
    if (!pendingJobId) return;

    setCurrentJobId(pendingJobId);
    setSearchMode('image');
    setSearchLoading(true);
    setSearchStatus({
      message: tRef.current('products.searchingByImage') || 'Searching by image...',
      type: 'loading',
    });
    setOverrideProducts(null);

    // Start polling
    startPolling(pendingJobId);

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // Polling logic — uses REST API
  // =========================
  const startPolling = (jobId: string) => {
    let attempt = 0;
    let authRetries = 0;
    const maxAttempts = 45; // 90 seconds (2s * 45)
    const maxAuthRetries = 5; // wait up to ~2.5s for auth to rehydrate after navigation

    const poll = async () => {
      attempt += 1;

      const apiJob = await pollImageSearchJobApi(jobId);

      if (!apiJob) {
        // Auth may not be rehydrated yet (e.g., after navigation) — retry
        authRetries += 1;
        if (authRetries <= maxAuthRetries) {
          pollTimerRef.current = setTimeout(poll, 500);
          return;
        }
        setSearchLoading(false);
        setSearchStatus({
          message: tRef.current('products.imageSearch.signInRequired') || 'Please sign in to use image search.',
          type: 'error',
        });
        setCurrentJobId(null);
        sessionStorage.removeItem('pendingJobId');
        return;
      }

      // Auth is ready — reset auth retries counter
      authRetries = maxAuthRetries + 1;

      const job = mapApiJobToJobData(apiJob);
      setJobData(job);

      // Handle completed
      if (job.status === 'completed') {
        const result = job.result || {};
        const rawItems = (result.items || result.products || []) as Record<string, unknown>[];
        const items: ParsedProduct[] = rawItems.map(edgeResultToParsedProduct);
        const total = (result.total as number | undefined) ?? items.length;
        setOverrideProducts(items);
        setSearchLoading(false);
        setSearchStatus({
          message: tRef.current('products.imageSearch.completed', { count: total }) || `${total} similar products found from your uploaded image.`,
          type: 'success',
        });
        setCurrentJobId(null);
        setJobData(null);
        sessionStorage.removeItem('pendingJobId');
        return;
      }

      // Handle failed
      if (job.status === 'failed') {
        setSearchLoading(false);
        setSearchStatus({
          message: job.error?.message || (tRef.current('products.imageSearch.failed') || 'Image search failed. Please try another image.'),
          type: 'error',
        });
        setCurrentJobId(null);
        setJobData(null);
        sessionStorage.removeItem('pendingJobId');
        return;
      }

      // Timeout check
      if (attempt > maxAttempts) {
        setSearchLoading(false);
        setSearchStatus({
          message: tRef.current('products.imageSearch.timeout') || 'Image search is taking longer than expected. You can keep this page open or try again.',
          type: 'error',
        });
        return;
      }

      // Still pending/processing — continue
      const pct = job.progress?.percent || 0;
      setSearchStatus({
        message: `${tRef.current('products.searchingByImage') || 'Searching by image...'} (${pct}%)`,
        type: 'loading',
      });
      pollTimerRef.current = setTimeout(poll, 2000);
    };

    pollTimerRef.current = setTimeout(poll, 1000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const {
    paginatedProducts,
    loading: productsLoading,
    error,
    filters,
    updateFilter,
    clearFilters,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    categoryCounts,
    currentPage,
    setCurrentPage,
    pageSize,
    totalPages,
    totalCount,
  } = useProducts(overrideProducts);

  // Determine active master code category from current filter state
  const activeCategoryCode: string | null = filters.category;

  // =========================
  // Apply URL search param to filter on mount (from homepage navigation)
  // =========================
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam && searchParam.trim()) {
      // Defer to next tick so useProducts has initialized filters
      const timer = setTimeout(() => {
        updateFilter('search', searchParam.trim());
        setSearchMode('text');
      }, 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleClearSearch = useCallback(() => {
    if (uploadedImageUrl) {
      URL.revokeObjectURL(uploadedImageUrl);
    }
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setSearchMode('browse');
    setSearchLoading(false);
    setSearchStatus(null);
    setOverrideProducts(null);
    setCurrentJobId(null);
    setJobData(null);
    setUploadedImageUrl(null);
    setPendingTextSearch(false);
    updateFilter('search', '');
    setCurrentPage(0);
    sessionStorage.removeItem('pendingJobId');
    sessionStorage.removeItem('imageSearchResults');
    sessionStorage.removeItem('pendingImageFileName');
    // Clear URL search param
    navigate('/packaging', { replace: true });
  }, [updateFilter, setCurrentPage, uploadedImageUrl, navigate]);

  // Clean up image search state when switching away from image search mode
  useEffect(() => {
    if (searchMode !== 'image') {
      if (uploadedImageUrl) {
        URL.revokeObjectURL(uploadedImageUrl);
        setUploadedImageUrl(null);
      }
      setCurrentJobId(null);
      setJobData(null);
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  }, [searchMode, uploadedImageUrl]);

  // ── Show search result status after server responds ──
  useEffect(() => {
    if (!pendingTextSearch || productsLoading) return;

    setPendingTextSearch(false);

    if (totalCount === 0) {
      setSearchStatus({
        message:
          t('products.noProductsFoundForQuery', { query: filters.search }) ||
          `No products found for "${filters.search}".`,
        type: 'error',
      });
    } else {
      setSearchStatus({
        message:
          t('products.textSearchResult', { count: totalCount, query: filters.search }) ||
          `${totalCount} products found for "${filters.search}".`,
        type: 'success',
      });
    }
  }, [pendingTextSearch, productsLoading, totalCount, filters.search, t]);

  const handleTextSearch = useCallback(() => {
    if (!filters.search.trim()) {
      handleClearSearch();
      return;
    }
    setSearchMode('text');
    setOverrideProducts(null);
    setCurrentPage(0);
    setPendingTextSearch(true);

    // Update URL with search query for shareable links
    navigate(`/packaging?search=${encodeURIComponent(filters.search.trim())}`, { replace: true });

    // Server fetch will be triggered by filter change;
    // status is shown via the pendingTextSearch effect below.
  }, [filters.search, handleClearSearch, setCurrentPage, navigate]);

  const handleImageSearchClick = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setShowSignupModal(true);
      return false;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setShowSignupModal(true);
      return false;
    }
    return true;
  }, []);

  const handleImageSearch = useCallback(async (file: File) => {
    console.log('[image-search] 1 submit started', { fileName: file.name, fileSize: file.size, fileType: file.type });

    // 1. Client-side validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const fileExt = (file.name.split('.').pop() || '').toLowerCase();
    const isAllowedType = allowedTypes.includes(file.type) || ['jpg', 'jpeg', 'png', 'webp'].includes(fileExt);
    if (!isAllowedType) {
      console.warn('[image-search] 1 validation FAILED: unsupported file type');
      setSearchStatus({
        message: tRef.current('products.imageSearch.invalidFile') || 'Only JPG, PNG, or WEBP files are supported.',
        type: 'error',
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      console.warn('[image-search] 1 validation FAILED: file too large', { fileSize: file.size });
      setSearchStatus({
        message: tRef.current('products.imageSearch.tooLarge') || 'Image must be under 10MB.',
        type: 'error',
      });
      return;
    }

    // 2. Auth check
    const supabase = getSupabaseClient();
    if (!supabase) {
      console.error('[image-search] 2 auth FAILED: supabase client not available');
      setSearchStatus({
        message: 'Supabase client not available',
        type: 'error',
      });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('[image-search] 2 auth FAILED: user not signed in');
      setShowSignupModal(true);
      return;
    }

    console.log('[image-search] 2 auth session ready', { userId: user.id });

    // Reset state
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setSearchMode('image');
    setSearchLoading(true);
    setSearchStatus({
      message: tRef.current('products.imageSearch.uploaded') || 'Image uploaded.',
      type: 'loading',
    });
    setOverrideProducts(null);

    const userId = user.id;
    const jobId = crypto.randomUUID();
    const filePath = `image-search/${userId}/${jobId}/source.webp`;
    const locale = i18n.language || 'en';

    console.log('[image-search] 3 job uuid created', { jobId, filePath, locale });

    // Normalize: WebP 변환 + EXIF 보정 + 1280px 리사이즈
    console.log('[image-search] 3.1 normalizing image...');
    let uploadFile: File;
    try {
      const normalized = await normalizeImageForSearch(file);
      uploadFile = normalized.file;
    } catch (normErr) {
      console.error('[image-search] 3.1 normalization FAILED', normErr);
      setSearchStatus({
        message: normErr instanceof Error ? normErr.message : 'Image processing failed.',
        type: 'error',
      });
      setSearchLoading(false);
      return;
    }

    console.log('[image-search] 3.1 normalization complete, uploading...');

    // 3. Upload to Storage
    console.log('[image-search] 4 storage upload started', { bucket: 'customer-image-search', filePath });
    const { error: uploadError } = await supabase.storage
      .from('customer-image-search')
      .upload(filePath, uploadFile, {
        contentType: 'image/webp',
        upsert: false,
      });

    if (uploadError) {
      console.error('[image-search] 4 storage upload FAILED', uploadError);
      setSearchStatus({
        message: uploadError.message,
        type: 'error',
      });
      setSearchLoading(false);
      setUploadedImageUrl(null);
      return;
    }

    console.log('[image-search] 5 storage upload completed');

    // Use local blob URL for instant display (no storage auth/CORS issues)
    const blobUrl = URL.createObjectURL(file);
    setUploadedImageUrl(blobUrl);

    // 4. Create job via REST API
    console.log('[image-search] 6 job API create starting', {
      jobId,
      sourceImagePath: filePath,
      apiBaseConfigured: Boolean(import.meta.env.VITE_API_BASE_URL),
    });

    let apiJob: ImageSearchJobApi;
    try {
      apiJob = await createImageSearchJobApi({
        jobId,
        sourceImageBucket: 'customer-image-search',
        sourceImagePath: filePath,
        locale,
        requestedFilters: {
          category: filters.category,
          capacities: filters.capacities,
          materials: filters.materials,
        },
      });
    } catch (jobErr) {
      const errMsg = jobErr instanceof Error ? jobErr.message : 'Failed to create search job.';
      console.error('[image-search] 6 job API create FAILED', {
        error: errMsg,
        jobId,
        sourceImagePath: filePath,
      });
      setSearchStatus({
        message: errMsg,
        type: 'error',
      });
      setSearchLoading(false);
      return;
    }

    console.log('[image-search] 7 job API response received', {
      jobId: apiJob.id,
      status: apiJob.status,
    });

    const jobData = mapApiJobToJobData(apiJob);
    setCurrentJobId(jobId);
    setJobData(jobData);

    setSearchStatus({
      message: tRef.current('products.imageSearch.jobCreated') || 'Image search job created.',
      type: 'loading',
    });

    // Start polling — the REST API backend handles job processing internally
    console.log('[image-search] 8 polling started', { jobId });
    startPolling(jobId);
  }, [i18n, filters]);

  // Keep handleImageSearch ref in sync for the homepage → products file pass-through
  useEffect(() => {
    handleImageSearchRef.current = handleImageSearch;
  }, [handleImageSearch]);

  // =========================
  // Homepage → Products: consume pending image file (module-level SPA pass-through)
  // =========================
  useEffect(() => {
    const pendingFile = consumePendingImageSearchFile();
    if (!pendingFile) return;

    // Store original file name for display in status banner
    sessionStorage.setItem('pendingImageFileName', pendingFile.name);

    // Defer to next tick so the component is fully mounted and auth is ready
    const timer = setTimeout(() => {
      handleImageSearchRef.current?.(pendingFile);
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategorySelect = useCallback((code: string | null) => {
    if (!code) {
      // "All" - reset to browse mode
      updateFilter('search', '');
      updateFilter('category', null);
      setCurrentPage(0);
      setSearchMode('browse');
      navigate('/packaging', { replace: true });
      return;
    }
    // Secondary category selected - filter by master_code
    updateFilter('search', '');
    updateFilter('category', code);
    setCurrentPage(0);
    setSearchMode('browse');
    navigate('/packaging', { replace: true });
  }, [navigate, updateFilter]);



  return (
    <div className="min-h-screen bg-white">
      {/* Top Search Bar */}
      <SearchHeader
        search={filters.search}
        onSearchChange={(v) => updateFilter('search', v)}
        onSearchSubmit={handleTextSearch}
        isSearching={searchLoading}
        searchType={searchMode}
        onImageSearch={handleImageSearch}
        onImageSearchClick={handleImageSearchClick}
      />

      {/* Search Status Banner */}
      {searchStatus && (
        <div className="w-full bg-white border-b border-ink-100">
          <div className="w-full px-4 md:px-6 lg:px-10 pb-3">
            <div
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm ${
                searchStatus.type === 'loading'
                  ? 'bg-cream-50 text-ink-500'
                  : searchStatus.type === 'error'
                    ? 'bg-red-50 text-red-600'
                    : 'bg-emerald-50 text-emerald-700'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {searchStatus.type === 'loading' && (
                  <i className="ri-loader-4-line animate-spin text-base" />
                )}
                {searchStatus.type === 'success' && searchMode === 'text' && (
                  <i className="ri-search-line text-base" />
                )}
                {(searchStatus.type === 'loading' || searchStatus.type === 'success') && searchMode === 'image' && uploadedImageUrl && (
                  <img
                    src={uploadedImageUrl}
                    alt="Uploaded reference"
                    className="w-[50px] h-[50px] object-cover rounded shrink-0"
                  />
                )}
                {(searchStatus.type === 'loading' || searchStatus.type === 'success') && searchMode === 'image' && !uploadedImageUrl && (
                  <i className="ri-image-line text-base" />
                )}
                {searchStatus.type === 'error' && (
                  <i className="ri-error-warning-line text-base" />
                )}
                <span className="truncate">
                  {searchMode === 'image'
                    ? (t('products.imageSearchLabel') || 'Image Search')
                    : (t('products.textSearchLabel') || 'Text Search')}
                  {': '}
                  {searchStatus.message}
                </span>
              </div>
              {searchStatus.type !== 'loading' && (
                <button
                  onClick={handleClearSearch}
                  className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-ink-200 text-sm font-medium text-ink-500 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all cursor-pointer"
                  title={t('products.clearSearch') || 'Clear search'}
                >
                  <i className="ri-close-line text-sm" />
                  <span className="whitespace-nowrap">{t('products.clearResult') || 'Clear result'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Error Banner */}
      {error && (
        <div className="mx-4 md:mx-6 lg:mx-10 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
          <i className="ri-error-warning-line text-base" />
          {error}
        </div>
      )}

      {/* Main Layout */}
      <div className="flex flex-col lg:flex-row w-full px-4 md:px-6 lg:px-10 py-4 md:py-6 gap-6 lg:gap-8">
        {/* Left Sidebar: Categories */}
        <aside className={`hidden lg:block shrink-0 transition-all duration-300 ${sidebarCollapsed ? 'w-14' : 'w-64'}`}>
          <div className="sticky top-24">
            <CategorySidebar
              categories={productCategories}
              activeCategoryCode={activeCategoryCode}
              onCategorySelect={handleCategorySelect}
              totalCount={totalCount}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
            />
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <FilterToolbar
            totalCount={totalCount}
          />

          <div className="mt-2">
            <ProductResults
              products={paginatedProducts}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              viewMode={viewMode}
              loading={productsLoading || searchLoading}
              onClearFilters={clearFilters}
              filters={filters}
            />
          </div>
        </div>
      </div>

      <BottomCTA />
      <Footer />

      <SignupRequiredModal
        open={showSignupModal}
        onClose={() => setShowSignupModal(false)}
      />
    </div>
  );
}